/**
 * Decimal-hours parsing and formatting for the logbook grid.
 *
 * `NZ-LOGBOOK.md` §3: time is stored in decimal hours to two places (the NZ
 * convention), but typed as `1:30`, `1.5` or `90m` — whichever a pilot reaches
 * for — and normalised on the way in. Rounding happens exactly once, here,
 * rather than compounding across a thousand entries.
 */

const ROUND_TO_HUNDREDTHS = 100;

/**
 * Decimal hours, to the two places everything downstream stores.
 *
 * Exported because parsing typed text is no longer the only way a number gets
 * into an entry: merging two entries adds their hours, and `0.1 + 0.2` in
 * binary floating point is not `0.3`. Rounding still happens in exactly one
 * place, which is the point.
 */
export function roundDecimalHours(hours: number): number {
  return Math.round(hours * ROUND_TO_HUNDREDTHS) / ROUND_TO_HUNDREDTHS;
}

const HOUR_MINUTE = /^(\d+):([0-5]?\d)$/;
const MINUTES_SUFFIX = /^(\d+(?:\.\d+)?)\s*m(?:in)?s?$/i;
const HOURS = /^(\d+(?:\.\d+)?)\s*h?$/i;

/**
 * Parse a pilot's typed time into decimal hours.
 *
 * @returns the value in decimal hours, or `null` if the text is not a time in
 *          any of the accepted shapes. An empty string is `0` — clearing a
 *          cell is how a pilot says "no time in this column".
 */
export function parseDurationHours(raw: string): number | null {
  const input = raw.trim();
  if (input === '') {
    return 0;
  }

  const hourMinute = HOUR_MINUTE.exec(input);
  if (hourMinute) {
    const [, h, m] = hourMinute;
    return roundDecimalHours(Number(h) + Number(m) / 60);
  }

  const minutes = MINUTES_SUFFIX.exec(input);
  if (minutes?.[1] !== undefined) {
    return roundDecimalHours(Number(minutes[1]) / 60);
  }

  const hours = HOURS.exec(input);
  if (hours?.[1] !== undefined) {
    return roundDecimalHours(Number(hours[1]));
  }

  return null;
}

export { formatDecimalHours } from '@/lib/format';
